import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Text } from '@hotjar/hotsauce';
import { LabelsEditor } from '@hotjar/labels';
import type { LabelModel } from '@hotjar/labels';
import { LabelsContext } from '@hotjar/webapp/contexts/labels';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';

import { useHighlightLabels } from '../use-highlight-labels';
import { SkeletonLoader } from './SkeletonLoader';
import { LabelsMultiSelect } from '../LabelsMultiSelect';

const HeadingSkeleton = styled(SkeletonLoader)`
  width: 110px;
  height: 20px;
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x0pt5};
`;

const LabelsSkeleton = styled(SkeletonLoader)`
  width: 228px;
  height: 48px;
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x0pt5};
`;

const Wrapper = styled.section`
  padding: ${tokens.spacer.x2};
  padding-top: 0;
`;

const Heading = styled(Text)`
  margin-bottom: ${tokens.spacer.x0};
  margin-top: ${tokens.spacer.x1};
`;

function createLabelsSet(labels: Array<{ id: LabelId }>): Set<LabelId> {
  return new Set(labels.map((label) => label.id));
}

type LabelId = string;

interface LabelsProps {
  isLoading: boolean;
  highlightId: string;
  highlightLabels: LabelModel[];
  siteId: string;
}

export function Labels({
  isLoading = false,
  highlightId,
  highlightLabels,
  siteId,
}: LabelsProps): JSX.Element {
  const { t } = useTranslation();
  const { add, remove } = useHighlightLabels(siteId, highlightId);
  const isCustomLabelsAllowed = useFeatureFlag('settings.custom-labels');

  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<LabelId>>(
    createLabelsSet(highlightLabels),
  );

  useEffect(() => {
    setSelectedLabelIds(createLabelsSet(highlightLabels));
  }, [highlightLabels]);

  if (isLoading) {
    return (
      <Wrapper>
        <HeadingSkeleton />
        <LabelsSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading as="p" textType="subtitle2">
        {t('highlights:single.labels')}
      </Heading>
      {isCustomLabelsAllowed ? (
        <LabelsMultiSelect siteId={siteId} itemId={highlightId} itemLabels={highlightLabels} />
      ) : (
        <LabelsContext>
          <LabelsEditor
            onAdd={add}
            onRemove={remove}
            selectedLabelIds={selectedLabelIds}
            onSelectedIdsChange={setSelectedLabelIds}
            siteId={siteId}
          />
        </LabelsContext>
      )}
    </Wrapper>
  );
}
